import type { QueryClient } from '@tanstack/react-query';
import type { GetMarketConfigsRow, GetMarketRow } from '../../generated/openapi-types.gen.js';
import { sdkEvents } from '../events.js';

export const MARKET_CONFIGS_QUERY_KEY = ['market-configs', 'detail'] as const;
export type MarketConfig = GetMarketConfigsRow['configs'][string];
export type MarketConfigCatalog = GetMarketConfigsRow['configs'];
export interface TradeMarketConfig {
  readonly marketConfigAddress: string;
  readonly quoteMint: string;
}

const parsedConfigs = new WeakMap<MarketConfig, ParsedMarketConfig>();
const validatedConfigs = new WeakSet<object>();
const numericFields = [
  'convictionRetentionPerSecondPpb',
  'convictionResolutionThresholdPpb',
  'creationThresholdD6',
  'minBackAmountQuote',
  'minQuoteInPerTrade',
  'initialVirtualQuote',
  'initialTokenSupply',
  'convictionBaselineReserveQuote',
  'resolutionThresholdFloorMarginPpb',
  'resolutionThresholdDecayDelaySecs',
  'resolutionThresholdDropIntervalSecs',
  'resolutionThresholdDropPpb',
] as const;
const optionalNumericFields = ['convictionReferenceMarginPpb'] as const;
const bpsFields = [
  'feeBpsBuy',
  'feeBpsSellNormal',
  'feeBpsSellPenaltyExtra',
  'feeBpsBacking',
  'feeBpsUnbacking',
  'feeBpsPrizePool',
  'feeSplitBackersBps',
  'feeSplitPlatformBps',
] as const;

export function isMarketConfigCatalog(value: unknown): value is GetMarketConfigsRow {
  if (value === null || typeof value !== 'object' || !('configs' in value)) return false;
  const configs = value.configs;
  return configs !== null && typeof configs === 'object' && !Array.isArray(configs);
}

export function marketConfigCatalog(value: unknown): MarketConfigCatalog | undefined {
  if (!isMarketConfigCatalog(value)) return undefined;
  const configs: Record<string, MarketConfig> = {};
  for (const [address, config] of Object.entries(value.configs)) {
    if (isMarketConfig(config)) configs[address] = config;
  }
  return configs;
}

function isMarketConfig(value: unknown): value is MarketConfig {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  if (validatedConfigs.has(value)) return true;
  const fields = value as Record<string, unknown>;
  for (const name of numericFields) {
    const number = fields[name];
    if (
      typeof number !== 'string' ||
      !/^(0|[1-9]\d*)$/.test(number) ||
      number.length > 20 ||
      (number.length === 20 && number > '18446744073709551615')
    )
      return false;
  }
  for (const name of optionalNumericFields) {
    const number = fields[name];
    if (number === undefined) continue;
    if (
      typeof number !== 'string' ||
      !/^(0|[1-9]\d*)$/.test(number) ||
      number.length > 20 ||
      (number.length === 20 && number > '18446744073709551615')
    )
      return false;
  }
  for (const name of bpsFields) {
    const number = fields[name];
    if (typeof number !== 'number' || !Number.isInteger(number) || number < 0 || number > 10000)
      return false;
  }
  if (
    typeof fields.quoteMint !== 'string' ||
    !/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(fields.quoteMint)
  )
    return false;
  if (
    typeof fields.minOutcomes !== 'number' ||
    typeof fields.maxOutcomes !== 'number' ||
    !Number.isInteger(fields.minOutcomes) ||
    !Number.isInteger(fields.maxOutcomes) ||
    fields.minOutcomes < 0 ||
    fields.minOutcomes > 255 ||
    fields.maxOutcomes < 0 ||
    fields.maxOutcomes > 255
  )
    return false;
  validatedConfigs.add(value);
  return true;
}
const refreshers = new WeakMap<QueryClient, (address: string, explicit: boolean) => void>();

export function requestMarketConfig(client: QueryClient, address: string, explicit = false): void {
  refreshers.get(client)?.(address, explicit);
}

/** One bounded automatic attempt per unseen address, shared by leaves and projector. */
export function installMarketConfigRefresh(
  client: QueryClient,
  refresh: () => Promise<unknown>,
): () => void {
  const attempted = new Set<string>();
  let disposed = false;
  let pending: Promise<unknown> | undefined;
  refreshers.set(client, (address, explicit) => {
    if (!explicit && (readMarketConfig(client, address) !== undefined || attempted.has(address)))
      return;
    if (!attempted.has(address) && !explicit)
      sdkEvents.emit('cache:diagnostic', {
        kind: 'market-config',
        reason: 'missing-address',
        count: attempted.size + 1,
      });
    attempted.add(address);
    pending ??= Promise.resolve()
      .then(() => {
        if (!disposed) return refresh();
      })
      .catch((error: unknown) => {
        attempted.clear();
        throw error;
      })
      .finally(() => {
        pending = undefined;
      });
    void pending.catch(() => {});
  });
  return () => {
    disposed = true;
    refreshers.delete(client);
  };
}

export interface ParsedMarketConfig {
  readonly convictionRetentionPerSecondPpb: bigint;
  readonly convictionResolutionThresholdPpb: bigint;
  readonly creationThresholdD6: bigint;
  readonly minBackAmountQuote: bigint;
  readonly minQuoteInPerTrade: bigint;
  readonly initialVirtualQuote: bigint;
  readonly initialTokenSupply: bigint;
  readonly convictionBaselineReserveQuote: bigint;
  readonly resolutionThresholdFloorMarginPpb: bigint;
  readonly resolutionThresholdDecayDelaySecs: bigint;
  readonly resolutionThresholdDropIntervalSecs: bigint;
  readonly resolutionThresholdDropPpb: bigint;
}

/** Derived values follow the lifetime of the one immutable catalog object. */
export function parseMarketConfig(config: MarketConfig): ParsedMarketConfig {
  const existing = parsedConfigs.get(config);
  if (existing !== undefined) return existing;
  const parsed = {
    convictionRetentionPerSecondPpb: BigInt(config.convictionRetentionPerSecondPpb),
    convictionResolutionThresholdPpb: BigInt(config.convictionResolutionThresholdPpb),
    creationThresholdD6: BigInt(config.creationThresholdD6),
    minBackAmountQuote: BigInt(config.minBackAmountQuote),
    minQuoteInPerTrade: BigInt(config.minQuoteInPerTrade),
    initialVirtualQuote: BigInt(config.initialVirtualQuote),
    initialTokenSupply: BigInt(config.initialTokenSupply),
    convictionBaselineReserveQuote: BigInt(config.convictionBaselineReserveQuote),
    resolutionThresholdFloorMarginPpb: BigInt(config.resolutionThresholdFloorMarginPpb),
    resolutionThresholdDecayDelaySecs: BigInt(config.resolutionThresholdDecayDelaySecs),
    resolutionThresholdDropIntervalSecs: BigInt(config.resolutionThresholdDropIntervalSecs),
    resolutionThresholdDropPpb: BigInt(config.resolutionThresholdDropPpb),
  };
  parsedConfigs.set(config, parsed);
  return parsed;
}

export function readMarketConfig(
  client: QueryClient,
  address: string | null | undefined,
): MarketConfig | undefined {
  const config =
    address == null
      ? undefined
      : client.getQueryData<GetMarketConfigsRow>(MARKET_CONFIGS_QUERY_KEY)?.configs?.[address];
  return isMarketConfig(config) ? config : undefined;
}

export function requireTradeMarketConfig(
  client: QueryClient,
  marketAddress: string,
): TradeMarketConfig {
  const market = client.getQueryData<GetMarketRow>(['markets', 'entity', marketAddress]);
  if (market == null || typeof market.marketConfig !== 'string') {
    throw new Error('Trade market config unavailable: market cache is missing marketConfig');
  }
  const config = readMarketConfig(client, market.marketConfig);
  if (config === undefined) {
    throw new Error('Trade market config unavailable: catalog entry is missing or incomplete');
  }
  return {
    marketConfigAddress: market.marketConfig,
    quoteMint: config.quoteMint,
  };
}
